import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RowDefinition } from '@calculom/report-designer';
import { useDesignerStore } from '../store';
import { MOCK_ACCOUNTS } from '../mock-data';

interface CanvasRowProps {
  row: RowDefinition;
  rowId: string;
  depth: number;
  isSelected: boolean;
}

function getRowDisplay(row: RowDefinition): string {
  switch (row.type) {
    case 'header':
      return row.text;
    case 'section':
      return `${row.name}${row.showSubtotal ? ' (with subtotal)' : ''}`;
    case 'account': {
      const acct = MOCK_ACCOUNTS.find((a) => a.code === row.accountCode);
      return acct ? `${acct.code} - ${acct.name}` : row.accountCode || '(no account selected)';
    }
    case 'subtotal':
      return row.label;
    case 'calculated':
      return `${row.label}${row.formula ? ` = ${row.formula}` : ''}`;
    case 'spacer':
      return '--- spacer ---';
    case 'line':
      return `── line (${row.borderStyle}) ──`;
    case 'grand_total':
      return row.label;
    case 'title_block':
      return `Title: ${row.text} (${row.alignment})`;
    case 'column_headers': {
      const label = row.label ? ` (${row.label})` : '';
      const colNames = row.columns.map((c) => c.header).join(' | ');
      return `── Columns${label}: ${colNames} ──`;
    }
  }
}

const TYPE_COLORS: Record<string, string> = {
  header: 'bg-blue-100 text-blue-800',
  section: 'bg-green-100 text-green-800',
  account: 'bg-indigo-100 text-indigo-800',
  subtotal: 'bg-amber-100 text-amber-800',
  calculated: 'bg-purple-100 text-purple-800',
  spacer: 'bg-gray-100 text-gray-600',
  line: 'bg-gray-200 text-gray-700',
  grand_total: 'bg-red-100 text-red-800',
  title_block: 'bg-cyan-100 text-cyan-800',
  column_headers: 'bg-teal-100 text-teal-800',
};

function CanvasRowComponent({ row, rowId, depth, isSelected }: CanvasRowProps) {
  const { selectRow, removeNode, indentNode, outdentNode } = useDesignerStore();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    paddingLeft: `${depth * 24 + 12}px`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        selectRow(row.id);
      }}
      className={`
        flex items-center gap-2 pr-3 py-2 rounded border transition-colors group
        ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}
        ${isDragging ? 'opacity-50 shadow-lg' : ''}
      `}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
        title="Drag to reorder"
      >
        &#x2630;
      </button>

      <span className="text-xs text-gray-400 font-mono flex-shrink-0 w-8 text-right">
        {rowId}
      </span>

      <span className={`inline-block px-2 py-0.5 text-xs rounded flex-shrink-0 ${TYPE_COLORS[row.type] ?? 'bg-gray-200 text-gray-700'}`}>
        {row.type}
      </span>

      <div className="flex-1 text-sm truncate text-gray-700">
        {getRowDisplay(row)}
      </div>

      {/* Indent/Outdent buttons - visible on hover or selected */}
      <div className={`flex gap-0.5 flex-shrink-0 ${isSelected ? 'visible' : 'invisible group-hover:visible'}`}>
        {depth > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              outdentNode(row.id);
            }}
            className="text-gray-400 hover:text-gray-700 text-xs p-1"
            title="Outdent (Shift+Tab)"
          >
            &#x2190;
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            indentNode(row.id);
          }}
          className="text-gray-400 hover:text-gray-700 text-xs p-1"
          title="Indent (Tab)"
        >
          &#x2192;
        </button>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          removeNode(row.id);
        }}
        className="text-red-400 hover:text-red-600 text-sm p-1 flex-shrink-0"
        title="Remove row"
      >
        &#x2715;
      </button>
    </div>
  );
}

export const CanvasRow = React.memo(CanvasRowComponent);
